import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Button, Table, Form, FormGroup, Label, Input } from 'reactstrap';
import styled, { keyframes } from 'styled-components';
import { classActions, userActions, studentActions } from '../../redux/actions';
import { ClassesContainer, FilterInput, HeaderButtonsContainer, StyledTable, TableContainer, WarningButton, DangerButton, StyledButton, SuccessButton, SecondaryButton } from './ClassesManage.styled';
import { FiUser, FiClock, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import { authHeader } from '../../helpers';

const FormWrapper = styled.div`
  margin-bottom: 20px;
  border: 2px solid #ccc;
  padding: 15px;
`;

const StyledTableHeader = styled.th`
  background-color: #f0f0f0;
  color: #333;
`;

const RequiredAsterisk = styled.span`
  color: red;
  margin-left: 5px;
`;

// ── Delete confirm modal ──────────────────────────────────────────────────────

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalCard = styled.div`
  background: #fff; border-radius: 18px; max-width: 480px; width: 100%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  animation: ${slideUp} 0.25s ease;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #ef4444, #dc2626);
  padding: 20px 24px; display: flex; align-items: center; gap: 12px;
  color: #fff;
  h3 { margin: 0; font-size: 18px; font-weight: 800; }
  p  { margin: 4px 0 0; font-size: 13px; opacity: 0.9; }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
`;

const SectionTitle = styled.div`
  font-size: 12px; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px;
`;

const ItemRow = styled.div<{ warn?: boolean; info?: boolean }>`
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border-radius: 8px; margin-bottom: 6px; font-size: 13px;
  background: ${p => p.info ? '#eff6ff' : p.warn ? '#fff7ed' : '#fef2f2'};
  border: 1px solid ${p => p.info ? '#bfdbfe' : p.warn ? '#fed7aa' : '#fecaca'};
  color: ${p => p.info ? '#1e40af' : p.warn ? '#c2410c' : '#991b1b'};
`;

const Count = styled.span`
  font-weight: 800; font-size: 15px;
`;

const Zero = styled.span`
  color: #9ca3af; font-size: 12px; font-style: italic;
`;

const ModalFooter = styled.div`
  padding: 16px 24px; display: flex; gap: 10px; justify-content: flex-end;
  border-top: 1px solid #f3f4f6;
`;

const CancelBtn = styled.button`
  padding: 10px 20px; border-radius: 10px; border: 1.5px solid #e5e7eb;
  background: #fff; color: #374151; font-weight: 600; font-size: 13px; cursor: pointer;
  &:hover { background: #f9fafb; }
`;

const DeleteBtn = styled.button`
  padding: 10px 24px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff;
  font-weight: 700; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Spinner = styled.div`
  width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff; border-radius: 50%; animation: spin 0.6s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ── Main component ────────────────────────────────────────────────────────────

const ClassesManage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [formVisible, setFormVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    teacherId: '',
    sessionFee: null as number | null,
    monthlyFee: null as number | null,
    note: '',
    id: null as number | null,
  });

  // Delete confirm state
  const [deletePreview, setDeletePreview] = useState<any | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const classes = useAppSelector((state: any) => state.classes.classesList);
  const teachers = useAppSelector((state: any) => state.users.items);
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);
  const students = useAppSelector((state: any) => state.students.studentsList);

  useEffect(() => {
    dispatch(userActions.getAllTeachers());
    dispatch(classActions.getAll());
    dispatch(studentActions.getAll());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const newValue = (name === 'sessionFee' || name === 'monthlyFee') && value === '' ? null : value;
    setFormData({ ...formData, [name]: newValue });
  };

  const getTeacherUserName = (teacherId: number): string | undefined => {
    const teacher = teachers.find((t: any) => t.id === teacherId);
    return teacher ? teacher.userName : undefined;
  };

  const isFormValid = () => formData.name.trim() !== '' && formData.teacherId !== '';

  const handleCreateClass = () => {
    if (!isFormValid()) return;
    dispatch(classActions.createClass(formData));
    resetForm();
  };

  const handleUpdateClass = () => {
    if (!isFormValid() || formData.id === null) return;
    dispatch(classActions.updateClass(formData));
    resetForm();
  };

  const handleClickDelete = async (classID: number) => {
    setPreviewLoading(true);
    setDeleteTargetId(classID);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/classes/${classID}/delete-preview`,
        { headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': '69420' } },
      );
      const data = await res.json();
      setDeletePreview(data);
    } catch {
      setDeletePreview(null);
      // Fallback: show simple confirm
      if (window.confirm('Xoá lớp học này?')) {
        dispatch(classActions.deleteClass(classID));
        resetForm();
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await dispatch(classActions.deleteClass(deleteTargetId) as any);
      resetForm();
    } finally {
      setDeleting(false);
      setDeletePreview(null);
      setDeleteTargetId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeletePreview(null);
    setDeleteTargetId(null);
  };

  const handleEditClass = (cls: any) => {
    setFormData({
      name: cls.name,
      teacherId: cls.teacherId,
      sessionFee: cls.sessionFee,
      monthlyFee: cls.monthlyFee,
      note: cls.note || '',
      id: cls.id,
    });
    setFormVisible(true);
  };

  const resetForm = () => {
    setFormData({ name: '', teacherId: '', sessionFee: null, monthlyFee: null, note: '', id: null });
    setFormVisible(false);
  };

  const filteredClasses = [...classes].filter((cls: any) => {
    const s = searchTerm.toLowerCase();
    return cls.name.toLowerCase().includes(s) || getTeacherUserName(cls.teacherId)?.includes(s);
  });

  const countStudentsByClass = (classId: number) => {
    const cs = students.filter((s: any) => s.classId === classId);
    return {
      active:  cs.filter((s: any) => s.startDate !== null).length,
      pending: cs.filter((s: any) => s.startDate === null).length,
    };
  };

  const totalActive  = students.filter((s: any) => s.startDate !== null).length;
  const totalPending = students.filter((s: any) => s.startDate === null).length;

  // Build preview rows
  const counts = deletePreview?.counts;
  const previewRows = counts ? [
    { label: 'Buổi điểm danh',      value: counts.attendanceSessions,    type: 'delete' },
    { label: 'Lịch học',             value: counts.schedules,             type: 'delete' },
    { label: 'Nhật kí dạy học',      value: counts.lessonDiaries,         type: 'delete' },
    { label: 'Bài ôn tập',           value: counts.reviewSessions,        type: 'delete' },
    { label: 'Buổi học theo tháng',  value: counts.classMonthlySession,   type: 'delete' },
    { label: 'Học phí chi tiết',     value: counts.tuitionDetails,        type: 'delete' },
    { label: 'Bộ flashcard',         value: counts.flashcardSets,         type: 'delete' },
    { label: 'Học sinh (tách khỏi lớp, không xoá hồ sơ)', value: counts.students, type: 'unlink' },
  ] : [];

  return (
    <ClassesContainer>
      <HeaderButtonsContainer>
        <div className="buttons-and-search">
          <StyledButton onClick={() => setFormVisible(true)} disabled={formVisible}>
            Tạo Lớp Học
          </StyledButton>
          <FilterInput
            type="text"
            placeholder="Tìm kiếm lớp học..."
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
          />
        </div>
      </HeaderButtonsContainer>

      {formVisible && (
        <FormWrapper>
          <Form>
            <FormGroup>
              <Label for="name">Tên Lớp<RequiredAsterisk>*</RequiredAsterisk></Label>
              <Input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} />
            </FormGroup>
            <FormGroup>
              <Label for="teacherId">Giáo viên<RequiredAsterisk>*</RequiredAsterisk></Label>
              <Input type="select" name="teacherId" id="teacherId" value={formData.teacherId} onChange={handleInputChange}>
                <option value="">Chọn Giáo viên</option>
                {teachers.map((teacher: any) => (
                  <option key={teacher.id} value={teacher.id}>{teacher.userName}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="sessionFee">Học phí 1 buổi (Nếu có):</Label>
              <Input type="number" name="sessionFee" id="sessionFee"
                value={formData.sessionFee !== null ? formData.sessionFee : ''} onChange={handleInputChange} />
            </FormGroup>
            <FormGroup>
              <Label for="monthlyFee">Học phí 1 tháng (Nếu có):</Label>
              <Input type="number" name="monthlyFee" id="monthlyFee"
                value={formData.monthlyFee !== null ? formData.monthlyFee : ''} onChange={handleInputChange} />
            </FormGroup>
            <FormGroup>
              <Label for="note">Ghi chú</Label>
              <Input type="textarea" name="note" id="note" value={formData.note} onChange={handleInputChange} />
            </FormGroup>
            <div style={{ display: 'flex', gap: '10px' }}>
              <SuccessButton onClick={formData.id !== null ? handleUpdateClass : handleCreateClass} disabled={!isFormValid()}>
                {formData.id !== null ? 'Cập Nhật Lớp Học' : 'Lưu Lớp Học'}
              </SuccessButton>
              <SecondaryButton onClick={resetForm}>Huỷ</SecondaryButton>
            </div>
          </Form>
        </FormWrapper>
      )}

      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <StyledTableHeader>ID</StyledTableHeader>
              <StyledTableHeader>Tên Lớp</StyledTableHeader>
              <StyledTableHeader>
                Sĩ số
                <div style={{ fontSize: '0.9em', color: '#555', marginTop: 2 }}>
                  <span style={{ marginRight: 8 }}><FiUser color="#28a745" /> {totalActive}</span>
                  <span style={{ color: '#ff9800' }}><FiClock color="#ff9800" /> {totalPending}</span>
                </div>
              </StyledTableHeader>
              <StyledTableHeader>Tên GVCN</StyledTableHeader>
              <StyledTableHeader>Học Phí theo buổi</StyledTableHeader>
              <StyledTableHeader>Học Phí theo tháng</StyledTableHeader>
              <StyledTableHeader>Ghi chú</StyledTableHeader>
              <StyledTableHeader>Cập Nhật</StyledTableHeader>
            </tr>
          </thead>
          <tbody>
            {filteredClasses
              .sort((a: any, b: any) => a.name.localeCompare(b.name))
              .map((cls: any) => {
                const { active, pending } = countStudentsByClass(cls.id);
                return (
                  <tr key={cls.id}>
                    <td>{cls.id}</td>
                    <td>{cls.name}</td>
                    <td>
                      <span style={{ marginRight: 8 }}><FiUser /> {active}</span>
                      <span style={{ color: '#999' }}><FiClock /> {pending}</span>
                    </td>
                    <td>{getTeacherUserName(cls.teacherId)}</td>
                    <td>{cls.sessionFee}</td>
                    <td>{cls.monthlyFee}</td>
                    <td>{cls.note}</td>
                    <td>
                      <WarningButton onClick={() => handleEditClass(cls)}>Sửa</WarningButton>{' '}
                      <DangerButton onClick={() => handleClickDelete(cls.id)} disabled={previewLoading && deleteTargetId === cls.id}>
                        {previewLoading && deleteTargetId === cls.id ? '...' : 'Xoá'}
                      </DangerButton>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </StyledTable>
      </TableContainer>

      {userInfo?.role == 0 && <p>Total: {filteredClasses.length}</p>}

      {/* ── Delete Confirm Modal ── */}
      {deletePreview && (
        <Overlay onClick={e => { if (e.target === e.currentTarget) handleCancelDelete(); }}>
          <ModalCard>
            <ModalHeader>
              <FiAlertTriangle size={28} />
              <div>
                <h3>Xoá lớp "{deletePreview.className}"?</h3>
                <p>Hành động này không thể hoàn tác. Dữ liệu sau sẽ bị xoá vĩnh viễn:</p>
              </div>
            </ModalHeader>

            <ModalBody>
              <SectionTitle>📋 Dữ liệu sẽ bị xoá</SectionTitle>

              {previewRows.map((row, i) => (
                row.value > 0 ? (
                  <ItemRow key={i} info={row.type === 'unlink'} warn={row.type === 'unlink'}>
                    <span>{row.label}</span>
                    <Count>{row.value}</Count>
                  </ItemRow>
                ) : (
                  <ItemRow key={i} style={{ background: '#f9fafb', border: '1px solid #f3f4f6' }}>
                    <span style={{ color: '#9ca3af' }}>{row.label}</span>
                    <Zero>Không có</Zero>
                  </ItemRow>
                )
              ))}

              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 10,
                background: '#fef3c7', border: '1px solid #fcd34d',
                fontSize: 12, color: '#92400e', lineHeight: 1.5,
              }}>
                ⚠️ <strong>Lưu ý:</strong> Hồ sơ học sinh <strong>không bị xoá</strong>,
                chỉ tách ra khỏi lớp (classId → null). Tất cả dữ liệu điểm danh, nhật kí,
                bài ôn tập liên quan đến lớp này sẽ mất vĩnh viễn.
              </div>
            </ModalBody>

            <ModalFooter>
              <CancelBtn onClick={handleCancelDelete}>Huỷ bỏ</CancelBtn>
              <DeleteBtn onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? <><Spinner /> Đang xoá...</> : <><FiTrash2 /> Xác nhận Xoá lớp</>}
              </DeleteBtn>
            </ModalFooter>
          </ModalCard>
        </Overlay>
      )}
    </ClassesContainer>
  );
};

export default ClassesManage;
